import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Hls from "hls.js";
import { MessageCircle, Radio, Send, Video } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LivePlayer({ src }) {
  const ref = useRef(null);
  const [playbackError, setPlaybackError] = useState("");

  useEffect(() => {
    const video = ref.current;
    if (!video || !src) return undefined;

    let hls;
    let destroyed = false;
    let playbackWatch;
    let lastTime = 0;
    let stalledTicks = 0;
    setPlaybackError("");
    video.crossOrigin = "anonymous";
    video.muted = true;

    const tryPlay = () => {
      if (destroyed) return;
      const promise = video.play();
      if (promise?.catch) promise.catch(() => {});
    };

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.addEventListener("loadedmetadata", tryPlay, { once: true });
    } else if (Hls.isSupported()) {
      hls = new Hls({
        lowLatencyMode: false,
        liveSyncDurationCount: 2,
        liveMaxLatencyDurationCount: 4,
        maxLiveSyncPlaybackRate: 1.25,
        manifestLoadingMaxRetry: 8,
        levelLoadingMaxRetry: 8,
        fragLoadingMaxRetry: 8,
        backBufferLength: 30,
        enableWorker: true,
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setPlaybackError("");
        tryPlay();
        playbackWatch = window.setInterval(() => {
          if (destroyed) return;
          const current = Number(video.currentTime || 0);
          const advancing = current > lastTime + 0.1;
          lastTime = current;
          stalledTicks = advancing ? 0 : stalledTicks + 1;
          // Keep long-running sessions near the live edge instead of silently
          // accumulating a minute of delay when the viewer falls behind.
          const edge = Number(hls.liveSyncPosition);
          if (Number.isFinite(edge) && edge > 0 && edge - current > 10 && video.readyState >= 2) {
            video.currentTime = edge;
            stalledTicks = 0;
          }
          if (stalledTicks >= 3) {
            stalledTicks = 0;
            setPlaybackError("Live video stalled. Reconnecting…");
            try { hls.stopLoad(); } catch {}
            try { hls.startLoad(-1); } catch {}
            tryPlay();
          }
        }, 3000);
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data?.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          setPlaybackError("Live video connection interrupted. Reconnecting…");
          window.setTimeout(() => {
            if (!destroyed) hls?.startLoad();
          }, 1200);
          return;
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          setPlaybackError("Live video decoder interrupted. Recovering…");
          try {
            hls.recoverMediaError();
          } catch {
            setPlaybackError("Live video could not be decoded in this browser.");
          }
          return;
        }
        setPlaybackError(data?.details ? `Live video error: ${data.details}` : "Live video could not start.");
      });

      const separator = src.includes("?") ? "&" : "?";
      hls.loadSource(`${src}${separator}viewer_ts=${Date.now()}`);
      hls.attachMedia(video);
    } else {
      setPlaybackError("This browser cannot play the live stream format.");
    }

    const onPlaying = () => setPlaybackError("");
    video.addEventListener("playing", onPlaying);

    return () => {
      destroyed = true;
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("loadedmetadata", tryPlay);
      if (playbackWatch) window.clearInterval(playbackWatch);
      if (hls) hls.destroy();
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, [src]);

  return (
    <div className="relative w-full h-full">
      <video ref={ref} controls autoPlay muted playsInline preload="auto" className="w-full h-full object-contain" />
      {playbackError && (
        <div className="absolute inset-x-4 bottom-14 rounded-lg bg-black/80 px-4 py-3 text-center text-sm text-white">
          {playbackError}
        </div>
      )}
    </div>
  );
}

export default function PublicLive() {
  const [live, setLive] = useState(null);
  const [chat, setChat] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [displayName, setDisplayName] = useState("");
  const [message, setMessage] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const [clockTick, setClockTick] = useState(0);
  const chatRef = useRef(null);

  const refresh = async () => {
    try {
      const [liveRes, chatRes, recordingsRes] = await Promise.all([
        axios.get(`${API}/communications/public-live`),
        axios.get(`${API}/communications/public-live/chat`),
        axios.get(`${API}/communications/public-live/recordings`),
      ]);
      setLive(liveRes.data || {});
      setChat(chatRes.data || []);
      setRecordings(recordingsRes.data || []);
    } catch {
      setLive({ is_live: false, title: "Nation Live" });
    }
  };

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chat]);

  useEffect(() => {
    if (!live?.is_live) return undefined;
    const timer = setInterval(() => setClockTick((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [live?.is_live]);

  const formatElapsed = () => {
    if (!live?.is_live || !live?.started_at) return null;
    const started = new Date(live.started_at).getTime();
    if (!Number.isFinite(started)) return null;
    const total = Math.max(0, Math.floor((Date.now() - started) / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return h > 0
      ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const getPublicChatClientId = () => {
    const storageKey = "yasharal_public_chat_client_id";
    let value = window.localStorage.getItem(storageKey);
    if (!value) {
      value = window.crypto?.randomUUID?.() || `viewer-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(storageKey, value);
    }
    return value;
  };

  const sendChat = async () => {
    if (sendingChat) return;
    const cleanName = displayName.trim();
    const cleanMessage = message.trim();
    if (!cleanName || !cleanMessage) {
      toast.error("Enter a display name and message.");
      return;
    }

    const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setSendingChat(true);
    setMessage("");
    setChat((current) => [...current, {
      id: pendingId,
      display_name: cleanName,
      message: cleanMessage,
      pending: true,
    }]);

    try {
      const response = await axios.post(`${API}/communications/public-live/chat`, {
        display_name: cleanName,
        message: cleanMessage,
        client_id: getPublicChatClientId(),
      });
      const saved = response.data || {};
      setChat((current) => current.map((item) => item.id === pendingId ? saved : item));
    } catch (error) {
      setChat((current) => current.filter((item) => item.id !== pendingId));
      setMessage(cleanMessage);
      toast.error(error?.response?.data?.detail || "Live chat is not open right now.");
    } finally {
      setSendingChat(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="public-live-page">
      <section className="bg-royal-blue text-white py-4">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold ${live?.is_live ? "bg-red-600" : "bg-slate-700"}`}>
              <Radio className={`w-4 h-4 ${live?.is_live ? "animate-pulse" : ""}`} /> {live?.is_live ? `LIVE${formatElapsed() ? ` • ${formatElapsed()}` : ""}` : "OFFLINE"}
            </span>
            <div>
              <h1 className="font-heading text-xl md:text-2xl">{live?.title || "Nation Live"}</h1>
              {live?.description && <p className="text-white/75 text-sm">{live.description}</p>}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">
          <div className="space-y-4">
            <div className="aspect-video bg-black rounded-xl overflow-hidden flex items-center justify-center shadow-lg">
              {live?.is_live && live?.hls_url ? (
                <LivePlayer src={live.hls_url} />
              ) : live?.poster_url ? (
                <img src={live.poster_url} alt="Upcoming Nation live" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-white/70 px-6">
                  <Video className="w-14 h-14 mx-auto mb-3 text-kingdom-gold" />
                  <p className="text-xl font-semibold">No public broadcast is live right now.</p>
                  <p className="text-sm mt-2">When a Nation broadcast begins, it will appear here automatically.</p>
                </div>
              )}
            </div>

            {live?.is_live && (
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wider font-semibold">Live Stream</p>
                <h2 className="font-heading text-2xl text-royal-blue mt-1">{live.title}</h2>
                {live.description && <p className="text-slate-600 mt-2">{live.description}</p>}
              </div>
            )}
          </div>

          <Card className="overflow-hidden lg:sticky lg:top-28">
            <div className="bg-slate-950 text-white px-4 py-3 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-kingdom-gold" />
              <span className="font-semibold">Live Chat</span>
            </div>
            <CardContent className="p-0">
              <div ref={chatRef} className="h-[430px] overflow-y-auto bg-slate-950 text-white p-4 space-y-3">
                {chat.length === 0 ? (
                  <p className="text-sm text-slate-500">No chat messages yet.</p>
                ) : chat.map((item) => (
                  <div key={item.id} className="text-sm">
                    <span className="font-bold text-kingdom-gold">{item.display_name}: </span>
                    <span className={item.pending ? "text-slate-400" : "text-slate-100"}>{item.message}</span>{item.pending && <span className="ml-2 text-[10px] text-slate-500">sending…</span>}
                  </div>
                ))}
              </div>
              <div className="p-4 space-y-2 border-t">
                <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Display name" maxLength={40} />
                <div className="flex gap-2 items-end">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendChat();
                      }
                    }}
                    placeholder="Join the live chat…"
                    maxLength={500}
                    rows={2}
                    className="flex-1 min-h-[42px] max-h-32 resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  />
                  <Button onClick={sendChat} disabled={sendingChat || !live?.is_live || !live?.chat_enabled} className="bg-royal-blue text-white"><Send className="w-4 h-4" /></Button>
                </div>
                <p className="text-[11px] text-slate-500">Enter sends · Shift+Enter adds a new line</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <section className="mt-12">
          <h2 className="font-heading text-2xl text-royal-blue mb-4">Past Broadcasts</h2>
          {recordings.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-slate-500">Public recordings will appear here.</CardContent></Card>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {recordings.map((recording) => (
                <Card key={recording.id} className="overflow-hidden">
                  <div className="aspect-video bg-slate-900">
                    {recording.thumbnail_url ? (
                      <img src={recording.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Video className="w-10 h-10 text-white/40" /></div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-royal-blue">{recording.title}</h3>
                    {recording.recorded_at && <p className="text-xs text-slate-500 mt-1">{new Date(recording.recorded_at).toLocaleString()}</p>}
                    <a href={recording.file_url} className="inline-block mt-3 text-sm font-semibold text-kingdom-gold">Watch recording →</a>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
